const DAY_MS = 24 * 60 * 60 * 1000;
const ISO_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function isValidIsoDay(iso: string): boolean {
  const m = ISO_DAY_RE.exec(iso);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12) return false;
  if (d < 1 || d > 31) return false;
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
}

export function isoToUtcAnchor(iso: string): Date {
  const m = ISO_DAY_RE.exec(iso);
  if (!m) throw new Error(`invalid iso day: ${iso}`);
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  return new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
}

function isoFromUtc(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

export function expandRange(start: string, end: string): string[] {
  if (!isValidIsoDay(start) || !isValidIsoDay(end)) return [];
  if (start > end) return [];
  const startMs = isoToUtcAnchor(start).getTime();
  const endMs = isoToUtcAnchor(end).getTime();
  const out: string[] = [];
  for (let t = startMs; t <= endMs; t += DAY_MS) {
    out.push(isoFromUtc(new Date(t)));
  }
  return out;
}

export function weekdayOf(iso: string): string {
  const date = isoToUtcAnchor(iso);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
  }).format(date);
}

export function isWeekend(iso: string): boolean {
  const w = weekdayOf(iso);
  return w === 'Sat' || w === 'Sun';
}

export function isWeekday(iso: string): boolean {
  return !isWeekend(iso);
}

export function monthOf(iso: string): string {
  return iso.slice(0, 7);
}

export function monthsInRange(start: string, end: string): string[] {
  if (!isValidIsoDay(start) || !isValidIsoDay(end) || start > end) return [];
  const a = isoToUtcAnchor(start);
  const b = isoToUtcAnchor(end);
  const out: string[] = [];
  let y = a.getUTCFullYear();
  let m = a.getUTCMonth();
  const endY = b.getUTCFullYear();
  const endM = b.getUTCMonth();
  while (y < endY || (y === endY && m <= endM)) {
    out.push(`${y}-${pad2(m + 1)}`);
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }
  return out;
}

export function formatDayLabel(iso: string): string {
  const d = isoToUtcAnchor(iso);
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(d);
}

export function formatMonthLabel(yyyymm: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(yyyymm);
  if (!m) return yyyymm;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 15, 12, 0, 0));
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

export function formatRangeLabel(start: string, end: string): string {
  if (start === end) return formatDayLabel(start);
  const a = isoToUtcAnchor(start);
  const b = isoToUtcAnchor(end);
  const sameMonthYear =
    a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth();
  if (sameMonthYear) {
    const left = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      day: 'numeric',
    }).format(a);
    const right = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      day: 'numeric',
      month: 'short',
    }).format(b);
    return `${left}–${right}`;
  }
  const left = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
  }).format(a);
  const right = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
  }).format(b);
  return `${left} – ${right}`;
}

export function isInRange(iso: string, start: string, end: string): boolean {
  return iso >= start && iso <= end;
}

export function rangeLength(start: string, end: string): number {
  return expandRange(start, end).length;
}

export function dayCellsForMonth(year: number, month: number): Array<string | null> {
  const first = new Date(Date.UTC(year, month, 1, 12, 0, 0));
  const lead = (first.getUTCDay() + 6) % 7;
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const out: Array<string | null> = [];
  for (let i = 0; i < lead; i += 1) out.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    out.push(`${year}-${pad2(month + 1)}-${pad2(d)}`);
  }
  while (out.length % 7 !== 0) out.push(null);
  return out;
}
